import { PointListService } from '@http/clients/reporting-app/point-list.service';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { formatNumbersCompact, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import { WidgetType } from '../../types/report-widget-type.enum';
import {
    marketsEditorConfig,
    opportunitySourceEditorConfig,
    projectDueDateEditorConfig,
    projectEndDateEditorConfig,
    projectStartDateEditorConfig,
    projectStatusEditorConfig
} from './shared/additional-filters';
import { salesMainFilters } from './shared/main-filters';

export const PointListSystems: IReportConfiguration<PointListService> = {
    base: {
        type: ReportType.PointListSystems,
        serviceType: PointListService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getPointsListSystemBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getPointsListSystemMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'avg', label: 'Avg' },
                        { name: 'min', label: 'Min' },
                        { name: 'med', label: 'Med' },
                        { name: 'max', label: 'Max' }
                    ],
                    rows: [
                        { label: 'Typical of', name: 'TypicalOf' },
                        { label: 'Unit Points', name: 'Points' },
                        { label: 'Unit Parts Qty', name: 'Parts' },
                        { label: 'Unit Material TC ($)', name: 'MaterialCost', format: 'currency' },
                        { label: 'Unit Labor ($)', name: 'LaborCost', format: 'currency' },
                        { label: 'Unit Total DC ($)', name: 'TotalCost', format: 'currency' }
                    ]
                }
            },
            {
                methodName: 'getPointsListSystemLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Systems by Total Direct Cost'
                    },
                    series: [
                        {
                            xKey: 'systemName',
                            yKey: 'totalMaterialCost',
                            yName: 'Total Material Cost ($)',
                            type: 'line'
                        },
                        {
                            xKey: 'systemName',
                            yKey: 'totalLaborCost',
                            yName: 'Total Labor Cost ($)',
                            type: 'line'
                        },
                        {
                            xKey: 'systemName',
                            yKey: 'totalDirectCost',
                            yName: 'Total Direct Cost ($)',
                            type: 'line'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        }
                    ]
                }
            },
            {
                methodName: 'getPointsListSystemColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            xKey: 'salesPersonName',
                            yKey: 'totalMaterialCost',
                            yName: 'Total Material Cost ($)',
                            type: 'bar'
                        },
                        {
                            xKey: 'salesPersonName',
                            yKey: 'totalLaborCost',
                            yName: 'Total Labor Cost ($)',
                            type: 'bar'
                        },
                        {
                            xKey: 'salesPersonName',
                            yKey: 'totalDirectCost',
                            yName: 'Total Direct Cost ($)',
                            type: 'bar'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.FourRowStack
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getPointsListSystemValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    { field: 'name', headerName: 'System Name', enableRowGroup: true, lockVisible: true },
                    {
                        field: 'usedTotal',
                        headerName: 'Total in Awarded Final Estimates (qty)',
                        enableRowGroup: true,
                        lockVisible: true
                    },
                    {
                        field: 'avgPerEstimate',
                        headerName: 'Average in Awarded Final Estimates (qty)',
                        enableRowGroup: true,
                        lockVisible: true
                    },
                    { field: 'avgTypicalOf', headerName: 'Avg Typical of', aggFunc: 'avg' },
                    { field: 'minTypicalOf', headerName: 'Min Typical of', aggFunc: 'sum' },
                    { field: 'medTypicalOf', headerName: 'Med Typical of', aggFunc: 'sum' },
                    { field: 'maxTypicalOf', headerName: 'Max Typical of', aggFunc: 'sum' },
                    { field: 'avgPoints', headerName: 'Avg Unit Points', aggFunc: 'avg' },
                    { field: 'minPoints', headerName: 'Min Unit Points', aggFunc: 'sum' },
                    { field: 'medPoints', headerName: 'Med Unit Points', aggFunc: 'sum' },
                    { field: 'maxPoints', headerName: 'Max Unit Points', aggFunc: 'sum' },
                    { field: 'avgParts', headerName: 'Avg Unit Parts Qty', aggFunc: 'avg' },
                    { field: 'minParts', headerName: 'Min Unit Parts Qty', aggFunc: 'sum' },
                    { field: 'medParts', headerName: 'Med Unit Parts Qty', aggFunc: 'sum' },
                    { field: 'maxParts', headerName: 'Max Unit Parts Qty', aggFunc: 'sum' },
                    {
                        field: 'avgMaterialCost',
                        headerName: 'Avg Unit Material TC ($)',
                        type: 'currency',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'minMaterialCost',
                        headerName: 'Min Unit Material TC ($)',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'medMaterialCost',
                        headerName: 'Med Unit Material TC ($)',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'maxMaterialCost',
                        headerName: 'Max Unit Material TC ($)',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'grandTotalMaterialCost',
                        headerName: 'Grand Total Material Cost ($)',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    { field: 'avgLaborCost', headerName: 'Avg Unit Labor ($)', type: 'currency', aggFunc: 'avg' },
                    { field: 'minLaborCost', headerName: 'Min Unit Labor ($)', type: 'currency', aggFunc: 'sum' },
                    { field: 'medLaborCost', headerName: 'Med Unit Labor ($)', type: 'currency', aggFunc: 'sum' },
                    { field: 'maxLaborCost', headerName: 'Max Unit Labor ($)', type: 'currency', aggFunc: 'sum' },
                    {
                        field: 'grandTotalLaborCost',
                        headerName: 'Grand Total Labor Cost ($)',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'avgTotalCost',
                        headerName: 'Avg Unit Total Direct Cost ($)',
                        type: 'currency',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'minTotalCost',
                        headerName: 'Min Unit Total Direct Cost ($)',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'medTotalCost',
                        headerName: 'Med Unit Total Direct Cost ($)',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'maxTotalCost',
                        headerName: 'Max Unit Total Direct Cost ($)',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'grandTotalCost',
                        headerName: 'Grand Total Direct Cost ($)',
                        type: 'currency',
                        aggFunc: 'sum'
                    }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...salesMainFilters],
        additional: [
            projectDueDateEditorConfig,
            projectStartDateEditorConfig,
            projectEndDateEditorConfig,
            projectStatusEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig
        ]
    }
};
